/**
 * Users Service
 * Integrates the users module with the backend API (pagination, search, delete, etc.)
 */

import { Pagination } from '../interfaces/pagination';
import { http } from './http.service';

export enum UserType {
  Resident = 0,
  Committee = 1,
}

export enum UserSortBy {
  Name = 'name',
  CreatedAt = 'createdat',
  UpdatedAt = 'updatedat',
  Settlement = 'settlement'
}

type SortDirection = 0 | 1;

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber?: string;
  email?: string;
  userType?: UserType;
  userTypeLabel?: string;
  status?: 'פעיל' | 'לא פעיל' | 'מושהה';
  createdAt?: string;
  updatedAt?: string;
  projectId?: number;
  apartmentNumber?: number;
  isResident?: boolean;
}

export interface UsersResponse extends Pagination<User> {
  searchTerm?: string;
}

export interface UsersError {
  success: false;
  error: string;
  message: string;
}

/** An admin moving a user: to another apartment of their building and/or to another building (projectId). */
export interface MoveUserPayload {
  projectId?: number;
  apartmentNumber: number;
  /** The user's role in the building they move to: without it a move to another building makes them a tenant. */
  type?: UserType;
}

interface UsersServiceType {
  getAllUsers(options?: FetchUsersParams): Promise<UsersResponse>;
  deleteUser(id: number): Promise<{ success: true; message: string }>;
  moveUser(id: number, payload: MoveUserPayload): Promise<void>;
  setUserType(id: number, type: UserType): Promise<User>;
}

interface UsersApiItem {
  id?: number;
  userId?: number;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  type?: number | string | null;
  userType?: number | string | null;
  created?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  projectId?: number;
  apartmentNumber?: number;
  isResident?: boolean;
}

interface UsersApiResponse {
  // Support both legacy and new structures
  items?: UsersApiItem[];
  data?: UsersApiItem[];
  meta?: {
    status: number;
    title: string;
    description: string;
    error: number;
    output: Record<string, unknown>;
  };
}

interface FetchUsersParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  userType?: UserType;
  sortBy?: UserSortBy;
  sortDirection?: SortDirection;
}

interface MapContext {
  pageNumber: number;
  pageSize: number;
  searchTerm: string;
}

class UsersServiceError extends Error implements UsersError {
  success: false = false;
  error: string;
  message: string;
  /** The failed request's response, so screens can read its status and `meta.description`. */
  response?: any;

  constructor(code: string, message: string, response?: any) {
    super(message);
    this.name = 'UsersServiceError';
    this.error = code;
    this.message = message;
    this.response = response;
  }
}

const USERS_ENDPOINT = '/Users';

const USER_TYPE_LABELS: Record<UserType, string> = {
  [UserType.Resident]: 'דייר',
  [UserType.Committee]: 'נציג',
};

const USER_TYPE_KEYWORDS: Record<UserType, string[]> = {
  [UserType.Resident]: ['resident'],
  [UserType.Committee]: ['committee'],
};

const isUserType = (value: unknown): value is UserType => {
  return (
    value === UserType.Resident || value === UserType.Committee
  );
};

const coerceNumericId = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};

const parseUserType = (value: unknown): UserType | undefined => {
  if (isUserType(value)) {
    return value;
  }

  if (typeof value === 'number' && isUserType(Number(value))) {
    return Number(value) as UserType;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }

    const numeric = Number(normalized);
    if (Number.isFinite(numeric) && isUserType(numeric)) {
      return numeric as UserType;
    }

    for (const [typeKey, keywords] of Object.entries(USER_TYPE_KEYWORDS)) {
      const type = Number(typeKey) as UserType;
      if (keywords.some(keyword => keyword === normalized)) {
        return type;
      }
    }
  }

  return undefined;
};

const buildUserTypeLabel = (type?: UserType): string | undefined => {
  if (typeof type === 'number') {
    return USER_TYPE_LABELS[type];
  }
  return undefined;
};

const normalizeUser = (item: UsersApiItem): User => {
  const firstName = typeof item.firstName === 'string' ? item.firstName.trim() : '';
  const lastName = typeof item.lastName === 'string' ? item.lastName.trim() : '';
  const userTypeRaw = item.type ?? item.userType;
  const userType = parseUserType(userTypeRaw);
  const phoneNumber = (typeof item.phone === 'string' ? item.phone : item.phoneNumber) || undefined;
  const email = typeof item.email === 'string' ? item.email : undefined;
  const canonicalId = coerceNumericId(item.id ?? item.userId);
  const fallbackName = [firstName, lastName].filter(Boolean).join(' ').trim() || email || phoneNumber || 'משתמש ללא שם';
  const createdAt = item.created ?? item.createdAt ?? undefined;

  return {
    id: canonicalId,
    firstName,
    lastName,
    fullName: fallbackName,
    phoneNumber,
    email,
    userType,
    userTypeLabel: buildUserTypeLabel(userType),
    createdAt,
    updatedAt: item.updatedAt ?? undefined,
    projectId: item.projectId,
    apartmentNumber: item.apartmentNumber,
    isResident: item.isResident
  };
};

// Newest first, as the projects list: a new registration is on the first page, not the last of ~200.
const byNewestFirst = (a: User, b: User): number =>
  (Date.parse(b.createdAt || '') || 0) - (Date.parse(a.createdAt || '') || 0) || b.id - a.id;

const mapUsersResponse = (payload: UsersApiResponse | undefined, ctx: MapContext): UsersResponse => {
  const rawItems = payload?.data ?? payload?.items ?? [];
  const items = rawItems.map(normalizeUser).sort(byNewestFirst);
  const totalCount = items.length;

  return {
    items,
    pageNumber: 1,
    pageSize: totalCount,
    totalCount,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
    searchTerm: ctx.searchTerm
  };
};

const buildErrorMessage = (message: string, error: unknown): string => {
  if (error instanceof UsersServiceError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return `${message}: ${error.message}`;
  }
  if (typeof error === 'string' && error.trim()) {
    return `${message}: ${error}`;
  }
  return message;
};

const handleUsersError = (code: string, message: string, error: unknown): never => {
  // A 401 already sent the admin to the login screen: pass its marker through untouched.
  if (error && typeof error === 'object' && 'isUnauthorized' in error) {
    throw error;
  }
  const formatted = buildErrorMessage(message, error);
  console.error(`[UsersService] ${formatted}`, error);
  throw new UsersServiceError(code, formatted, (error as any)?.response);
};

const fetchUsers = async ({
  pageNumber = 1,
  pageSize = 10,
  searchTerm = '',
  userType,
  sortBy = UserSortBy.CreatedAt,
  sortDirection = 1
}: FetchUsersParams = {}): Promise<UsersResponse> => {
  try {
    // No params passed to backend - fetch all
    const params = {};

    // Remove pagination params from backend request to fetch all data
    const payload = await http.get<UsersApiResponse>(USERS_ENDPOINT, { params });
    return mapUsersResponse(payload, {
      pageNumber,
      pageSize,
      searchTerm
    });
  } catch (error) {
    return handleUsersError('FETCH_USERS_ERROR', 'Failed to fetch users', error);
  }
};

// PUT / DELETE /users/{id} answer 404 for a user deleted meanwhile (a stale row in the list): retrying can't help,
// the list has to be read again.
export const isUserNotFoundError = (error: unknown): boolean => (error as any)?.response?.status === 404;
export const USER_NOT_FOUND_MESSAGE = 'המשתמש לא נמצא – ייתכן שנמחק בינתיים. הרשימה עודכנה.';

// The buildings that have members but no representative (from the users list): nobody there can present
// professionals, run surveys or select suppliers until an admin appoints one.
export const findProjectsWithoutRepresentative = (users: User[]): Set<number> => {
  const withMembers = new Set<number>();
  const withRepresentative = new Set<number>();
  users.forEach(user => {
    if (user.projectId == null) return;
    withMembers.add(user.projectId);
    if (user.userType === UserType.Committee) withRepresentative.add(user.projectId);
  });
  return new Set(Array.from(withMembers).filter(projectId => !withRepresentative.has(projectId)));
};

const UsersService: UsersServiceType = {
  async getAllUsers(options: FetchUsersParams = {}): Promise<UsersResponse> {
    const {
      pageNumber = 1,
      pageSize = 10,
      searchTerm = '',
      userType,
      sortBy = UserSortBy.CreatedAt,
      sortDirection = 1,
    } = options;
    return fetchUsers({ pageNumber, pageSize, searchTerm, userType, sortBy, sortDirection });
  },

  // PUT /users/{id} as an admin moves the user into that apartment (it must be free): 400 INVALID_APARTMENT_NUMBER
  // when the building has no such apartment, 409 APARTMENT_ALREADY_TAKEN when someone joined it, 400
  // APARTMENT_NUMBER_REQUIRED without one. Moving to another building takes the user out of the old one and ends their
  // sessions.
  async moveUser(id: number, payload: MoveUserPayload): Promise<void> {
    try {
      // The move dialog shows the errors on its fields.
      await http.put<void>(`${USERS_ENDPOINT}/${id}`, payload, { silentErrors: true });
    } catch (error) {
      return handleUsersError('MOVE_USER_ERROR', `Failed to move user ${id}`, error);
    }
  },

  // PUT /users/{id} {type} as an admin: makes the user a representative of their building (1) or a tenant (0), e.g.
  // when a building's only representative left. It ends the user's sessions: they log in again with the new role.
  // Answers the user (admin shape).
  async setUserType(id: number, type: UserType): Promise<User> {
    try {
      const response = await http.put<any>(`${USERS_ENDPOINT}/${id}`, { type }, { silentErrors: true });
      const item = response && typeof response === 'object' && 'data' in response && !('id' in response)
        ? response.data
        : response;
      return normalizeUser(item || { id, type });
    } catch (error) {
      return handleUsersError('SET_USER_TYPE_ERROR', `Failed to change the type of user ${id}`, error);
    }
  },

  async deleteUser(id: number): Promise<{ success: true; message: string }> {
    try {
      // The users list toasts a refusal itself.
      await http.delete<void>(`${USERS_ENDPOINT}/${id}`, null, { silentErrors: true });
      return {
        success: true,
        message: 'המשתמש נמחק בהצלחה'
      };
    } catch (error) {
      return handleUsersError('DELETE_USER_ERROR', `Failed to delete user ${id}`, error);
    }
  }
};

export default UsersService;

